import React from "react";
import type { ComponentType } from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import { Controller } from "stimulus";
import { BotBuilderApp } from "@circle-react/apps/BotBuilderApp";
import { EoyRecapApp } from "@circle-react/apps/EoyRecapApp";
import { App } from "@circle-react/apps/MainApp";
import { CommunitySwitcherWithProviders } from "@circle-react/components/CommunitySwitcher";
import { CookieStatus } from "@circle-react/components/CookieStatus";
import { DebugSessions } from "@circle-react/components/CustomLogins/DebugSessions";
import { GDPRCookieBanner } from "@circle-react/components/GDPR/GDPRCookieBanner";
import { SearchWrapper as SearchV2 } from "@circle-react/components/HeaderV2/Search/SearchWrapper";
import { InvitationLinkInformation } from "@circle-react/components/InvitationLink/InvitationLinkInformation";
import { LandingPageSettings } from "@circle-react/components/LandingPageSettings";
import { MemberDirectory } from "@circle-react/components/MemberDirectory";
import { PaywallGroups as PaywallGroupsAdmin } from "@circle-react/components/PaywallGroups/Admin";
import { BulkImportTrialPaywallSubscriptionFormRailsView } from "@circle-react/components/Paywalls/Admin/BulkImportTrialPaywallSubscriptionForm/BulkImportTrialPaywallSubscriptionFormRailsView";
import { PaywallChargesAdminIndex } from "@circle-react/components/Paywalls/Admin/PaywallChargesAdmin";
import { PaywallSubscriptionsAdminIndex } from "@circle-react/components/Paywalls/Admin/PaywallSubscriptionsAdmin";
import { PaywallsAdmin } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin";
import { Checkout } from "@circle-react/components/Paywalls/Checkout";
import { PaywallPrices } from "@circle-react/components/Paywalls/PaywallPrices";
import { PostViewList } from "@circle-react/components/PostsV3/PostViewList";
import { Show } from "@circle-react/components/PostsV3/Show";
import { OpenQPModal } from "@circle-react/components/QuickPost/OpenQPModal";
import { QPEditPost } from "@circle-react/components/QuickPost/QPEditPost";
import RichTextInput from "@circle-react/components/RichTextInput";
import { AnalyticsHeader } from "@circle-react/components/SettingsApp/AnalyticsHeader";
import { LiveStreamSettingsV1 as LiveStreamSettings } from "@circle-react/components/SettingsApp/LiveStreamSettings/LiveStreamSettingsV1";
import { SsoInfoMessage } from "@circle-react/components/SettingsApp/Sso/SsoInfoMessage";
import { CreateSpaceButton } from "@circle-react/components/Spaces/CreateSpaceButton";
import { EditSpaceButton } from "@circle-react/components/Spaces/EditSpaceButton";
import { New } from "@circle-react/components/Spaces/New";
import { CommunityMemberSelect } from "@circle-react/components/shared/uikit/CommunityMemberSelect";
import { withMainAppProviders } from "@circle-react/providers/MainApp/withMainAppProviders";
import { AppBarWrapper } from "@circle-react/wrappers/AppBarWrapper";
import { TipTapBlockEditor } from "@circle-react-uikit/TipTapBlockEditor";
import { TranscriptsPlayer } from "@circle-react-uikit/TranscriptsPlayer";

/**
 * Used on <%= react_component_tag "ComponentName" %> tags in ERB files
 */
const COMPONENT_MAP: Record<string, ComponentType<any>> = {
  RichTextInput,
  CommunitySwitcherWithProviders,
  HomePage: () => null,
  WelcomeBanner: () => null,
  HomeRightSidebar: () => null,
  OpenQPModal,
  QPEditPost,
  LandingPageSettings,
  CookieStatus,
  CreateSpaceButton,
  EditSpaceButton,
  PostsPostViewList: PostViewList,
  SearchV2,
  "Spaces.New": New,
  "Posts.Show": Show,
  MemberDirectory,
  App: App,
  CommunityMemberSelect,
  PaywallPrices,
  GDPRCookieBanner,
  Checkout,
  AppBarWrapper,
  StandaloneTipTapBlockEditor: withMainAppProviders(TipTapBlockEditor),
  PaywallsAdmin,
  PaywallGroupsAdmin,
  PaywallSubscriptionsAdminIndex,
  PaywallChargesAdminIndex,
  BulkImportTrialPaywallSubscriptionFormRailsView,
  InvitationLinkInformation,
  DebugSessions,
  VideoPlayer: TranscriptsPlayer,
  LiveStreamSettings,
  AnalyticsHeader,
  SsoInfoMessage,
  BotBuilderApp,
  EoyRecapApp,
};

const jsonParse = (value: unknown): Record<string, unknown> => {
  if (typeof value !== "string") {
    return {};
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
};

export default class extends Controller {
  private root: Root | null = null;

  override connect() {
    if (!(this.element instanceof HTMLElement)) {
      console.error("Error: element is not an HTMLElement");
      return;
    }

    const { component: componentName, props: stringProps } =
      this.element.dataset;

    const props = jsonParse(stringProps);
    const Component = componentName ? COMPONENT_MAP[componentName] : null;

    if (!Component) {
      console.error(`Error: invalid component - ${componentName}`);
      return;
    }

    this.root = createRoot(this.element);
    this.root.render(React.createElement(Component, props));
  }

  override disconnect() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}
